/**
 * Unified analytics surface
 *
 * The one set of functions apps wire at boot: `initAnalytics()` once, after
 * runtime config resolves, and SessionContextProvider's `onIdentify`/`onReset`
 * to the `identifyUser` / `resetUser` exported here. PostHog no-ops when it
 * isn't configured (empty key), so this is safe to call unconditionally.
 *
 * Direct-capture helpers stay imported at their call sites from `./posthog`:
 * `captureError` for caught exceptions and `captureBugReport` for what a person
 * reports. Both land in PostHog error tracking, beside the automatic ones.
 */

import * as posthog from './posthog';

export function initAnalytics(config: {
  /** PostHog project API key. Empty disables analytics and error tracking. */
  posthogKey?: string;
  /** PostHog ingestion host (defaults to EU cloud inside initPostHog). */
  posthogHost?: string;
  /** The deployed environment ("development", "production") errors name. */
  environment?: string;
}): void {
  posthog.initPostHog({
    apiKey: config.posthogKey,
    host: config.posthogHost,
    environment: config.environment,
  });
}

/** Identify the user for analytics and error tracking. */
export function identifyUser(user: {
  id: string;
  email?: string;
  tenantId?: string;
  projectId?: string;
}): void {
  posthog.identifyUser(user);
}

/** Clear identity on logout. */
export function resetUser(): void {
  posthog.resetUser();
}

/** Capture a custom product-analytics event (PostHog). */
export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  posthog.captureEvent(event, properties);
}
